// `useDecompose` — Phase 3b. Calls POST /issues/:iid/decompose over SSE,
// surfaces three-phase progress (decomposing → scoring → done), and
// resolves with the flattened children proposals the IssueEditor's
// ProposalsPanel renders in children-mode.

import { useCallback, useRef, useState } from "react";

import {
  DecomposeResponseSchema,
  type DecomposeChild,
  type DecomposePhase,
  type DecomposeProgressEvent,
  type DecomposeResponse,
} from "@loom/shared/dashboard";

import { UnauthorizedError, signalUnauthorized } from "../lib/http";

export interface DecomposeProgress {
  readonly phase: DecomposePhase;
  readonly step: string;
  readonly message?: string;
}

export interface DecomposeState {
  readonly run: (issueIid: number) => Promise<DecomposeResponse>;
  readonly running: boolean;
  readonly progress: DecomposeProgress | null;
  readonly children: ReadonlyArray<DecomposeChild>;
  readonly methodologySkipped: boolean;
  readonly error: string | null;
  readonly reset: () => void;
}

const parseSseEvent = (
  chunk: string,
): { event: string; data: string } | null => {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of chunk.split("\n")) {
    if (line.startsWith(":")) continue;
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join("\n") };
};

export const useDecompose = (projectId: number): DecomposeState => {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<DecomposeProgress | null>(null);
  const [children, setChildren] = useState<ReadonlyArray<DecomposeChild>>([]);
  const [methodologySkipped, setMethodologySkipped] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef(false);

  const reset = useCallback((): void => {
    setProgress(null);
    setChildren([]);
    setMethodologySkipped(false);
    setError(null);
  }, []);

  const run = useCallback(
    async (issueIid: number): Promise<DecomposeResponse> => {
      if (inflight.current) throw new Error("decompose already in flight");
      inflight.current = true;
      setRunning(true);
      setError(null);
      setProgress(null);
      setChildren([]);
      setMethodologySkipped(false);
      try {
        const res = await fetch(
          `/api/projects/${projectId}/issues/${issueIid}/decompose`,
          {
            method: "POST",
            credentials: "same-origin",
            headers: { accept: "text/event-stream" },
          },
        );
        if (res.status === 401) {
          // This fetch never touches `jsonOrThrow` (SSE, not JSON), so the
          // app's one sign-in reaction has to be fired by hand or the lapse is
          // silent here.
          signalUnauthorized(
            `/api/projects/${projectId}/issues/${issueIid}/decompose`,
            await res.text().catch(() => ""),
          );
          throw new UnauthorizedError();
        }
        if (!res.ok || !res.body) {
          const detail = await res.text().catch(() => "");
          throw new Error(
            `${res.status} ${res.statusText}${detail ? `: ${detail}` : ""}`,
          );
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let final: DecomposeResponse | null = null;
        let streamErr: string | null = null;
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          let sep = buffer.indexOf("\n\n");
          while (sep >= 0) {
            const chunk = buffer.slice(0, sep);
            buffer = buffer.slice(sep + 2);
            sep = buffer.indexOf("\n\n");
            const parsed = parseSseEvent(chunk);
            if (!parsed) continue;
            if (parsed.event === "progress") {
              try {
                const evt = JSON.parse(parsed.data) as DecomposeProgressEvent;
                setProgress({
                  phase: evt.phase,
                  step: evt.step,
                  ...(evt.message !== undefined ? { message: evt.message } : {}),
                });
              } catch {
                // ignore malformed progress frames
              }
            } else if (parsed.event === "done") {
              const data: unknown = JSON.parse(parsed.data);
              final = DecomposeResponseSchema.parse(data);
            } else if (parsed.event === "error") {
              try {
                const errData = JSON.parse(parsed.data) as { message?: string };
                streamErr = errData.message ?? "stream_error";
              } catch {
                streamErr = "stream_error";
              }
            }
          }
        }
        if (streamErr !== null) throw new Error(streamErr);
        if (final === null) throw new Error("stream_ended_without_done");
        setChildren(final.children);
        setMethodologySkipped(final.methodologySkipped);
        return final;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setError(msg);
        throw err;
      } finally {
        inflight.current = false;
        setRunning(false);
      }
    },
    [projectId],
  );

  return {
    run,
    running,
    progress,
    children,
    methodologySkipped,
    error,
    reset,
  };
};
